import './index.css'

import clsx from 'clsx'
import React, { useRef, useState } from 'react'

import api from '@disco/api/server'
import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import captureError from '@disco/utils/captureError'

import ChargebeeScriptLoader from 'components/chargebee-form/chargebee-loader'
import { getFormValue } from 'components/chargebee-form/ChargebeeForm'
import { ChargebeeFormInputs } from 'components/chargebee-form/ChargebeeFormInputs'
import ChargebeeFormErrModal from 'components/chargebee-form/error-modal'
import Row from 'components/common/row'
import ModalActionDialog from 'components/modals/dialogs/modal-action-dialog'
import Button from 'components/ui/button'
import TextField from 'components/ui/text-field'

import { useDispatch } from 'hooks/use-redux'

type Props = {
  onRequestCloseUpdateModal: (...args: Array<any>) => any
  setUpdateModal: (...args: Array<any>) => any
}

const CardForm = ({
  onRequestCloseUpdateModal,
  setUpdateModal,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const [sendFormWaiting, setSendFormWaiting] = useState(false)
  const [chargebeeLoaded, setChargebeeLoaded] = useState(false)
  const [chargebeeError, setChargebeeError] = useState(false)
  // Chargebee throws an error with useState when setting the tokenize ref
  const chargebeeComponentRef = useRef(null)
  const chargebeeForm = useRef(null)

  const createErrModal = error => (
    <ChargebeeFormErrModal
      error={error}
      onRequestClose={() => setUpdateModal(null)}
    />
  )

  const successModal = (
    <ModalActionDialog
      type="success"
      header="Success!"
      submitButtonCaption="Ok"
      onSubmit={onRequestCloseUpdateModal}
      onClose={onRequestCloseUpdateModal}
    >
      Thanks! Your payment details have been updated.
    </ModalActionDialog>
  )

  const handleSubmit = async e => {
    e.preventDefault()

    if (!chargebeeComponentRef.current.tokenize) {
      return
    }

    setSendFormWaiting(true)

    const formValue: {
      first_name?: string
      last_name?: string
    } = getFormValue(chargebeeForm.current, 'INPUT')

    try {
      const newToken = await chargebeeComponentRef.current.tokenize({
        firstName: formValue.first_name,
        lastName: formValue.last_name,
      })

      await api.createPaymentProfileChargebeeForm({
        card_token: newToken.token,
      })
      setUpdateModal(successModal)
      setSendFormWaiting(false)
      dispatch(loadBusinessSubscription())
    } catch (error) {
      setUpdateModal(
        createErrModal({
          errors: error?.errors,
        }),
      )

      setSendFormWaiting(false)
      captureError(error, {
        description: 'chargebee.token() error inside BillingUpdate',
      })
    }
  }

  const onChargebeeLoad = () => {
    setChargebeeLoaded(true)
    setChargebeeError(false)
  }

  const onChargebeeError = () => {
    setChargebeeError(true)
  }

  return (
    <>
      <ChargebeeScriptLoader
        onLoad={onChargebeeLoad}
        onError={onChargebeeError}
      />

      <div
        className={clsx('CardForm', {
          'CardForm--Hidden': !chargebeeLoaded || chargebeeError,
        })}
      >
        <form ref={chargebeeForm}>
          <div id="chargebee-form">
            <Row columnsSize={[6, 6]}>
              <div>
                <TextField
                  required
                  name="first_name"
                  label="First Name on Account"
                  patternErrorMessage="This field is required"
                />
              </div>
              <div>
                <TextField
                  required
                  name="last_name"
                  label="Last Name on Account"
                  patternErrorMessage="This field is required"
                />
              </div>
            </Row>
            <ChargebeeFormInputs
              chargebeeLoaded={chargebeeLoaded}
              onReady={chargebeeComponent =>
                (chargebeeComponentRef.current = chargebeeComponent)
              }
            />
          </div>

          <Button
            appearance="primary"
            caption="Update"
            onClick={handleSubmit}
            progressIndicator={sendFormWaiting}
            progressCaption="Updating"
            disabled={sendFormWaiting || !chargebeeLoaded}
          />
        </form>
      </div>
    </>
  )
}

export default CardForm
